/**
 * Moderation Archive — operational-archive spec helpers.
 * Keeps the same Moderation Case ID as Queue (no separate Archive ID).
 * Status is Decided. Decision Outcome is Confirmed or Not Confirmed (Transfer is excluded).
 */

import { getArchiveCases } from './postModerationScenarios';
import { getDecidedModerationCases } from '../utils/moderationCaseSession';
import {
  archiveIdFromQueueCaseId,
  buildCapturedEvidence,
  buildFlaggedItems,
  buildQueueOverviewModel,
  decisionIdFromCaseId,
  findScenarioForCase,
  formatQueueDateTime,
  getModerationDecisionChecklist,
  normalizeQueueCase,
  normalizeQueueSource,
  normalizeQueueType,
  normalizeSupportTeam,
  resolveCaseHeader,
  stripModerationCasePrefix,
} from './moderationQueueSpec';

const dash = (value) => {
  if (value == null) return '—';
  const text = String(value).trim();
  return text === '' || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined'
    ? '—'
    : text;
};

export const resolveDecisionOutcome = (row = {}, scenario) => {
  const raw = String(
    row.decisionOutcome ||
      scenario?.case?.outcome ||
      row.finalDecision ||
      row.enforcementApplied ||
      row.enforcement ||
      ''
  ).toLowerCase();
  if (!raw) return null;
  if (raw.includes('request more') || raw.includes('more information') || raw.includes('unable')) {
    return null;
  }
  // Transfer / escalation cases are not archived in Moderation Archive.
  if (raw.includes('transfer') || raw.includes('escalat')) return null;
  if (
    raw.includes('not approved') ||
    raw.includes('not confirmed') ||
    raw.includes('no violation') ||
    raw === 'none' ||
    raw === 'no action' ||
    raw.includes('no action')
  ) {
    return 'Not Confirmed';
  }
  if (
    raw.includes('approved') ||
    raw.includes('confirmed') ||
    raw.includes('hide') ||
    raw.includes('remove') ||
    raw.includes('warning') ||
    raw.includes('suspend') ||
    raw.includes('unpublish') ||
    raw.includes('draft')
  ) {
    return 'Confirmed';
  }
  return null;
};

const resolveArchiveSource = (row = {}, scenario) => {
  if (row.source === 'Customer' || row.source === 'Business' || row.source === 'System') {
    return row.source;
  }
  const fromScenario = normalizeQueueSource(row, scenario);
  if (fromScenario === 'Customer' || fromScenario === 'Business' || fromScenario === 'System') {
    return fromScenario;
  }
  const raw = String(row.caseSource || row.source || '').toLowerCase();
  if (raw.includes('system') || raw.includes('detection')) return 'System';
  if (raw.includes('business')) return 'Business';
  return 'Customer';
};

const resolveArchiveType = (row = {}, scenario) =>
  normalizeQueueType(
    {
      ...row,
      contentType: row.queueType || row.type || row.contentType,
    },
    scenario
  );

export const normalizeArchiveCase = (row = {}, scenario) => {
  const type = resolveArchiveType(row, scenario);
  if (!type) return null;

  const decisionOutcome = resolveDecisionOutcome(row, scenario);
  if (!decisionOutcome) return null;

  const base =
    normalizeQueueCase(
      {
        ...row,
        status: 'Decided',
        contentType: type,
        assignedTo: row.assignedTo || row.closedBy || row.supportTeam || scenario?.case?.assignedTo,
      },
      scenario,
      { includeDecided: true }
    ) || {};

  const source = resolveArchiveSource({ ...row, ...base }, scenario);
  const finalisedOn =
    row.finalisedOn ||
    row.closedDate ||
    scenario?.case?.closedDate ||
    scenario?.case?.updated ||
    row.updated;
  const supportTeam = normalizeSupportTeam(
    row.supportTeam ||
      row.assignedTo ||
      scenario?.case?.assignedTo ||
      row.closedBy ||
      scenario?.case?.closedBy
  );
  const rawCaseId = base.caseId || row.caseId || row.queueCaseId;
  const archiveCaseId = rawCaseId ? archiveIdFromQueueCaseId(rawCaseId) : null;
  const queueCaseId =
    row.queueCaseId ||
    (rawCaseId ? archiveIdFromQueueCaseId(rawCaseId) : null);
  const moderationDecisionId =
    row.moderationDecisionId ||
    row.decisionId ||
    (queueCaseId || archiveCaseId ? decisionIdFromCaseId(queueCaseId || archiveCaseId) : '—');

  return {
    ...row,
    ...base,
    caseId: archiveCaseId,
    queueCaseId,
    moderationCaseId: archiveCaseId,
    moderationDecisionId,
    source,
    queueSource: source,
    type,
    queueType: type,
    contentType: type,
    caseReason: dash(base.caseReason || row.flagReason || scenario?.flag?.reason || scenario?.flag?.trigger),
    severity: dash(base.severity || row.severity || scenario?.case?.severity || scenario?.flag?.severity),
    status: 'Decided',
    decisionOutcome,
    supportTeam,
    assignedTo: supportTeam,
    finalisedOn,
    finalisedBy: row.finalisedBy || row.closedBy || scenario?.case?.closedBy || '—',
    received: row.caseCreated || row.created || scenario?.case?.created,
    created: row.caseCreated || row.created || scenario?.case?.created,
    updated: finalisedOn,
    lastUpdated: finalisedOn,
    accountName: (() => {
      const fromBase = base.accountName != null ? String(base.accountName).trim() : '';
      const fromRow = row.accountName != null ? String(row.accountName).trim() : '';
      const fromBusiness =
        row.businessName != null ? String(row.businessName).trim() : '';
      if (fromBase && fromBase !== '—') return fromBase;
      if (fromRow && fromRow !== '—') return fromRow;
      if (fromBusiness && fromBusiness !== '—') return fromBusiness;
      return '—';
    })(),
    accountKind: base.accountKind || row.accountType || row.accountKind,
    accountType: base.accountKind || row.accountType || row.accountKind,
    businessId: base.businessId || row.businessId || row.accountId,
    customerId: base.customerId || row.customerId,
    flagId: dash(base.flagId || row.flagId || scenario?.flag?.flagId),
    sourceRecordId: dash(base.sourceRecordId || row.contentId || row.postId || row.reviewId || row.messageId || row.quoteId),
    relatedAppealId: row.linkedAppealCaseId || row.relatedAppealId || null,
    postTitle: row.postTitle || row.contentTitle || scenario?.post?.title,
    quoteTitle: row.quoteTitle || row.contentTitle || scenario?.quote?.title,
    businessName: row.businessName || row.accountName || scenario?.business?.businessName,
  };
};

export const collectArchiveCases = () => {
  const raw = [...getArchiveCases(), ...getDecidedModerationCases()];
  const seen = new Set();
  return raw
    .map((row) =>
      normalizeArchiveCase(row, findScenarioForCase(row.queueCaseId || row.caseId))
    )
    .filter((row) => {
      if (!row || !row.caseId || row.caseId === '—') return false;
      if (seen.has(row.caseId)) return false;
      seen.add(row.caseId);
      return true;
    })
    .sort((a, b) => new Date(b.finalisedOn || 0) - new Date(a.finalisedOn || 0));
};

export const findArchiveCase = (caseId) => {
  if (!caseId) return null;
  const n = stripModerationCasePrefix(caseId);
  return (
    collectArchiveCases().find(
      (row) =>
        stripModerationCasePrefix(row.caseId) === n ||
        stripModerationCasePrefix(row.queueCaseId) === n
    ) || null
  );
};

export const matchesArchiveSearch = (row, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    row.caseId,
    row.queueCaseId,
    row.moderationCaseId,
    row.moderationDecisionId,
    row.flagId,
    row.accountName,
    row.sourceRecordId,
    row.relatedAppealId,
    row.linkedAppealCaseId,
    row.businessId,
    row.customerId,
    row.snapshotRef,
    row.flaggedItemRefs,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

const resolveArchiveSupportTeam = (row = {}) => {
  if (row.finalisedBy && row.finalisedBy !== '—' && row.finalisedBy !== 'Unassigned') {
    return row.finalisedBy;
  }
  return normalizeSupportTeam(row.supportTeam || row.assignedTo || row.finalisedBy);
};

export const ARCHIVE_DECISION_OUTCOME_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'Confirmed', label: 'Confirmed' },
  { value: 'Not Confirmed', label: 'Not Confirmed' },
];

export const ARCHIVE_FINALISED_DATE_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'Last 7 Days', label: 'Last 7 Days' },
  { value: 'Last 30 Days', label: 'Last 30 Days' },
  { value: 'Last 90 Days', label: 'Last 90 Days' },
];

export const matchesArchiveFilters = (row, filters = {}) => {
  if (filters.type && filters.type !== 'All' && (row.type || row.queueType) !== filters.type) {
    return false;
  }
  if (filters.decisionOutcome && filters.decisionOutcome !== 'All') {
    if (String(row.decisionOutcome || '') !== filters.decisionOutcome) return false;
  }
  if (filters.dateRange && filters.dateRange !== 'All') {
    if (!matchesFinalisedDate(row, filters)) return false;
  }
  return true;
};

export const getArchiveSupportTeamFilterOptions = (rows = []) => {
  const names = [
    ...new Set(
      rows
        .map((row) => resolveArchiveSupportTeam(row))
        .filter((name) => name && name !== 'Unassigned' && name !== '—')
    ),
  ].sort((a, b) => a.localeCompare(b));
  return [
    { value: 'All', label: 'All' },
    { value: 'Unassigned', label: 'Unassigned' },
    ...names.map((name) => ({ value: name, label: name })),
  ];
};

export const matchesFinalisedDate = (row, filters = {}) => {
  if (!filters.dateRange) return true;
  const finalised = row.finalisedOn ? new Date(row.finalisedOn) : null;
  if (!finalised || Number.isNaN(finalised.getTime())) return false;

  const now = new Date();
  now.setHours(23, 59, 59, 999);

  if (filters.dateRange === 'Last 7 Days') {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 7);
    cutoff.setHours(0, 0, 0, 0);
    return finalised >= cutoff && finalised <= now;
  }
  if (filters.dateRange === 'Last 30 Days') {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 30);
    cutoff.setHours(0, 0, 0, 0);
    return finalised >= cutoff && finalised <= now;
  }
  if (filters.dateRange === 'Last 90 Days') {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 90);
    cutoff.setHours(0, 0, 0, 0);
    return finalised >= cutoff && finalised <= now;
  }
  if (filters.dateRange === 'Custom Range') {
    if (!filters.dateFrom && !filters.dateTo) return true;
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom);
      from.setHours(0, 0, 0, 0);
      if (finalised < from) return false;
    }
    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      to.setHours(23, 59, 59, 999);
      if (finalised > to) return false;
    }
    return true;
  }
  return true;
};

export const buildArchiveOverviewModel = ({
  scenario,
  itemFromState,
  formatDateTime = formatQueueDateTime,
}) => {
  const item = normalizeArchiveCase(itemFromState || {}, scenario) || itemFromState || {};
  const model = buildQueueOverviewModel({
    scenario,
    itemFromState: item,
    formatDateTime,
  });
  const decisionId =
    item.moderationDecisionId && item.moderationDecisionId !== '—'
      ? item.moderationDecisionId
      : null;
  const sourceFlagId = item.flagId && item.flagId !== '—' ? item.flagId : model.linked?.sourceFlagId;
  const sourceRecordId =
    item.sourceRecordId && item.sourceRecordId !== '—'
      ? item.sourceRecordId
      : model.linked?.sourceRecordId;

  return {
    ...model,
    status: 'Decided',
    supportTeam: normalizeSupportTeam(
      item.finalisedBy || item.supportTeam || model.supportTeam
    ),
    finalisedBy: item.finalisedBy && item.finalisedBy !== 'Unassigned' ? item.finalisedBy : '—',
    moderationDecisionId: decisionId,
    finalisedOn: formatDateTime(item.finalisedOn),
    decisionOutcome: item.decisionOutcome,
    received: formatDateTime(item.received || item.created || model.received),
    linked: {
      ...model.linked,
      moderationDecisionId: decisionId,
      sourceFlagId: sourceFlagId || null,
      sourceRecordId: sourceRecordId || null,
      previousModerationCaseId:
        item.previousModerationCaseId || model.linked?.previousModerationCaseId || null,
      relatedAppealId: item.relatedAppealId || model.linked?.relatedAppealId || null,
      relatedSupportTicketId:
        item.relatedSupportTicketId || model.linked?.relatedSupportTicketId || null,
      // Transfer creates a new F&S entity — Moderation does not retain a linked F&S case/decision.
      fraudSecurityCaseId: null,
      fraudSecurityDecisionId: null,
      capturedSnapshotReference:
        item.snapshotRef || model.linked?.capturedSnapshotReference || null,
      exactFlaggedItemRefs: item.flaggedItemRefs || model.linked?.exactFlaggedItemRefs || null,
      warningRestrictionHistory: model.linked?.warningRestrictionHistory || null,
    },
    evidence: buildCapturedEvidence(item, scenario),
  };
};

const resolveDecisionAction = (row = {}, scenario) => {
  if (row.decisionActionLabel) return row.decisionActionLabel;
  if (row.decisionActionType) {
    const raw = String(row.decisionActionType);
    if (raw === 'confirm_decision' || raw === 'hide' || raw === 'approve') return 'Approve';
    if (raw === 'submit_rejection' || raw === 'restore_content') return 'Not Approve';
    if (raw === 'unable_to_determine' || raw === 'request_more_information') {
      return 'Request More Information';
    }
    if (raw === 'transfer_to_fraud_security') return 'Transfer';
    return raw;
  }
  const outcome = String(row.decisionOutcome || scenario?.case?.outcome || '').toLowerCase();
  if (outcome.includes('transfer') || outcome.includes('escalat')) return 'Transfer';
  if (outcome.includes('request more') || outcome.includes('unable')) {
    return 'Request More Information';
  }
  if (outcome.includes('not approve') || outcome.includes('not confirm') || outcome.includes('no violation')) {
    return 'Not Approve';
  }
  if (outcome.includes('approve') || outcome.includes('confirm')) return 'Approve';
  return '—';
};

export const buildArchivedDecision = (row = {}, scenario) => {
  const item = normalizeArchiveCase(row, scenario) || row;
  const outcome = item.decisionOutcome || 'Not Approved';
  const findings = buildFlaggedItems(item, scenario).map((flagged) => ({
    ...flagged,
    finding: outcome,
  }));
  const checklist = getModerationDecisionChecklist().map((check) => ({
    ...check,
    checked: true,
  }));
  return {
    moderationDecisionId: item.moderationDecisionId,
    decisionOutcome: outcome,
    decisionActionType: item.decisionActionType || null,
    decisionActionLabel: resolveDecisionAction(item, scenario),
    appliedActions: item.appliedActions || [],
    appliedActionsLabel: item.appliedActionsLabel || null,
    findings,
    flaggedItems: findings,
    checks: checklist,
    decisionReason: dash(item.decisionReason || scenario?.case?.decisionReason),
    fraudSecurityCaseId: null,
    finalisedBy: item.finalisedBy && item.finalisedBy !== 'Unassigned' ? item.finalisedBy : '—',
    finalisedOn: item.finalisedOn,
  };
};

export const buildArchiveAuditLog = (row = {}, scenario) => {
  const item = normalizeArchiveCase(row, scenario) || row;
  if (Array.isArray(item.auditEvents) && item.auditEvents.length) {
    return [...item.auditEvents].sort(
      (a, b) => new Date(a.dateTime || 0) - new Date(b.dateTime || 0)
    );
  }
  const caseId = item.caseId || '—';
  const rows = [];
  if (item.created || item.received) {
    rows.push({
      dateTime: item.created || item.received,
      event: 'Case receipt',
      details: 'System; case entered Queue',
      relatedRecord: caseId,
    });
  }
  if (item.finalisedOn) {
    const actor = item.finalisedBy && item.finalisedBy !== 'Unassigned' ? item.finalisedBy : '—';
    rows.push({
      dateTime: item.finalisedOn,
      event: 'Final decision',
      details: `${actor}; ${item.decisionOutcome}`,
      relatedRecord: item.moderationDecisionId || caseId,
    });
    rows.push({
      dateTime: item.finalisedOn,
      event: 'Movement to Archive',
      details: `${actor}; Decided`,
      relatedRecord: caseId,
    });
  }
  return rows.sort((a, b) => new Date(a.dateTime || 0) - new Date(b.dateTime || 0));
};

export const buildArchiveNotes = (row = {}, scenario) => {
  if (Array.isArray(row.notes) && row.notes.length) return row.notes;
  if (!scenario?.adminNote) return [];
  return [
    {
      id: 'archive-note',
      text: scenario.adminNote,
      addedBy: row.finalisedBy || scenario?.case?.closedBy || '—',
      addedOn: row.finalisedOn || scenario?.case?.updated || scenario?.case?.closedDate,
    },
  ];
};

export { formatQueueDateTime, resolveCaseHeader };
